import React, { useContext, MouseEvent } from 'react';
import cx from 'classnames';
import type { LoadType } from 'sharedTypes/loads/loadType';
import { UserContext } from 'components/Providers/Providers';
import { MediaQueryContext } from '@arrive/responsive-provider'

import Avatar from '@arrive/avatar';
import SVGIcon from '@arrive/svg-icon';

import ARRIVE_FOOTER_LOGO from 'assets/arrive-logo-footer.svg';
import { PricingFormatter } from 'helpers/formatters';
import { COMMITTED_CAPACITY_PENDO_ROOT } from 'constants/cPCommittedCapacityConstants';

import styles from './ContactRepSection.scss';

const capitalizeCorrectlyRegex = /(\b[a-z](?!\s))/g

const capitalizeWord = (word: string) => {
  return (word || '').toLowerCase().replace(capitalizeCorrectlyRegex, letter => letter.toUpperCase());
}

export const CompactContactRow = ({ className, avatar, sectionTitle, name, contactNumber, contactEmail, contactEmailHref }: { avatar: React.ReactNode; sectionTitle: string; name: string; contactNumber?: string; contactEmail?: string; contactEmailHref?: string; className?: string }) => {
  return (
    <div className={cx(styles.compactContactContainer, className)}>
      {avatar}
      <div className={styles.contactContainer}>
        <div className={styles.sectionTitle}>{sectionTitle}</div>
        <div className={styles.name} title={name}>{name}</div>
        {contactNumber && <a className={styles.callRepLink} href={`tel:+1${contactNumber}`} rel="noopener noreferrer" target='_blank'>{contactNumber}</a>}
        {contactEmail && <a className={styles.emailRepLink} href={contactEmailHref} rel="noopener noreferrer" target='_blank'>{contactEmail}</a>}
      </div>
    </div>
  )
}

export const CompactContactRep = ({ loadNumber, pickupCity, pickupState, deliveryCity, deliveryState }: { loadNumber: number; pickupCity: string; pickupState: string; deliveryCity: string; deliveryState: string }) => {
  const currentUser = useContext(UserContext);

  const emailSubjectLine = `[Carrier%20Inquiry]%20Load%20%23${loadNumber}%20-%20${capitalizeWord(pickupCity)},%20${pickupState}%20to%20${capitalizeWord(deliveryCity)},%20${deliveryState}`;

  if (currentUser.CarrierRep) {
    const rep = currentUser.CarrierRep;

    return (
      <CompactContactRow
        avatar={<Avatar className={styles.repAvatar} size='md' pictureUrl={rep.PictureUrl} firstName={rep.FirstName} lastName={rep.LastName} />}
        sectionTitle='Carrier Rep'
        name={rep.FullName}
        contactNumber={rep.UserPhoneNumber}
        contactEmail={rep.EmailAddress}
        contactEmailHref={`mailto:${rep.EmailAddress}?subject=${emailSubjectLine}${rep.TeamEmailAddress ? '&cc=' + rep.TeamEmailAddress : ''}`}
      />
    )
  }

  return (
    <CompactContactRow
      avatar={<div className={styles.arriveAvatar}><SVGIcon svgRef={ARRIVE_FOOTER_LOGO} /></div>}
      sectionTitle='Contact Arrive'
      name='Arrive Logistics'
      contactNumber='(888) 861-0650'
      contactEmail='coverage@arrivelogistics.com'
      contactEmailHref={`mailto:coverage@arrivelogistics.com?subject=${emailSubjectLine}`}
    />
  )
}

interface BookItNowContactRepProps {
    load: LoadType;
    btnSize: 'md' | 'lg';
    theme?: 'roadhog' | 'ethanol';
    type?: 'primary' | 'secondary';
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export const BookItNowContactRep = ({ load, btnSize, onClick, theme = 'roadhog', type = 'secondary' }: BookItNowContactRepProps) => {
  const loadNumber = load.LoadBoardId;
  const pickupCity = load.PickupEarlyCity;
  const pickupState = load.PickupEarlyStateCode;
  const deliveryCity = load.DeliveryLateCity;
  const deliveryState = load.DeliveryLateStateCode;

  const currentUser = useContext(UserContext);
  const currentMediaTypes = useContext(MediaQueryContext);
  let contactRepLink = '';
  const emailSubjectLine = `[Carrier%20Inquiry]%20Load%20%23${loadNumber}%20-%20${capitalizeWord(pickupCity)},%20${pickupState}%20to%20${capitalizeWord(deliveryCity)},%20${deliveryState}`;


  if (currentUser.CarrierRep) {
    const rep = currentUser.CarrierRep;
    if (currentMediaTypes.xs) {
      contactRepLink = `tel:+1${rep.UserPhoneNumber}`
    } else {
      contactRepLink = `mailto:${rep.EmailAddress}?subject=${emailSubjectLine}${rep.TeamEmailAddress ? '&cc=' + rep.TeamEmailAddress : ''}`
    }
  } else {
    if (currentMediaTypes.xs) {
      contactRepLink = 'tel:+18888610650'
    } else {
      contactRepLink = `mailto:coverage@arrivelogistics.com?subject=${emailSubjectLine}`
    }
  }

  return (
    <a
      href={contactRepLink}
      className={
        cx('arrive_Button__button', `arrive_Button__${btnSize}`, styles.contactRepCenter, {
          'arrive_Button__roadhog': theme === 'roadhog',
          'arrive_Button__buttonSecondary': type === 'secondary',
          'arrive_Button__buttonPrimary': type === 'primary'
        })}
      onClick={onClick}
      target='_blank'
      rel="noopener noreferrer"
    >
            Contact Rep
    </a>
  )
}

export function CommittedCapacityContactRepSection({ origin, destination, rate }: { origin: string | null, destination: string | null, rate: number | null }) {
  const currentUser = useContext(UserContext);

  const onEmailContactRep = () => {
    if (window.pendo) {
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_ROOT} Contact Rep Email`, {
        RepEmail: currentUser?.CarrierRep?.EmailAddress || 'coverage@arrivelogistics.com'
      });
    }
  };

  const emailSubjectLine = (!!origin && !!destination && !!rate) ? `[CCommitted%20Capacity%20Inquiry]%20${origin}%20to%20${destination}%20${PricingFormatter.format(rate)}` : `[Committed%20Capacity%20Inquiry]`;

  if (currentUser.CarrierRep) {
    const rep = currentUser.CarrierRep;

    return (
      <>
        <Avatar className={styles.repAvatar} size='md' pictureUrl={rep.PictureUrl} firstName={rep.FirstName} lastName={rep.LastName} />
        <span data-testid={'contact-rep-message'}>Contact {rep.FullName} to make changes to your committed lane.</span>
        <a
          className={cx(styles.callRepLink, styles.ccCallRepLink)}
          data-testid={'contact-rep-phone'}
          href={`tel:+1${rep.UserPhoneNumber}`}
          rel="noopener noreferrer"
          target='_blank'
        >
          {rep.UserPhoneNumber}
        </a>
        <a
          className={styles.emailRepLink}
          data-testid={'contact-rep-email'}
          href={`mailto:${rep.EmailAddress}?subject=${emailSubjectLine}${rep.TeamEmailAddress ? '&cc=' + rep.TeamEmailAddress : ''}`}
          rel="noopener noreferrer"
          target='_blank'
          onClick={() => onEmailContactRep()}
        >
          {rep.EmailAddress}
        </a>
      </>
    )
  }

  return (<></>)
}
